import React, {useEffect, useMemo, useState} from "react";
import { useTranslation, useI18next } from 'gatsby-plugin-react-i18next';
import { isRtlLanguage } from "../utils/rtl";

import api from "../utils/api";

import {Line} from "react-chartjs-2";

import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
} from "chart.js";

import {useCookies} from "react-cookie";
import SelectArrow from "../images/selectArrow.svg";
import {
	diaryRowsHaveRounds,
	groupDiaryRounds,
	roundStory,
	summarizeRound,
} from "../utils/weightDiary";

ChartJS.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend
);

const chartOptions = {
	plugins: {
		legend: {
			display: false,
		},
	},
	maintainAspectRatio: false,
};

export default function WeightGraphDiary() {
	const { t } = useTranslation();
	const { language } = useI18next();
	const isRtl = isRtlLanguage(language);
	const valueGap = isRtl ? "mr-1 md:mr-2" : "ml-1 md:ml-2";
	const [rounds, setRounds] = useState([]);
	const [diary, setDiary] = useState([]);
	const [openRounds, setOpenRounds] = useState({});

	const [cookies] = useCookies(["token", "id"]);
	const [token] = useState(cookies["token"]);
	const [idUser] = useState(cookies["id"]);

	useEffect(() => {
		const graphPromise = api.getWeightGraph(token, {id: idUser}, language);
		const diaryPromise = api.getWeightDiaryTable(token, {id: idUser}, language)
			.catch(() => ({changeTableData: []}));

		Promise.all([graphPromise, diaryPromise]).then(([res, table]) => {
			setRounds(chartRounds(res));
			setDiary((table && table.changeTableData) || []);
		});
	}, [idUser, token, language]);

	const stories = useMemo(() => storiesByRound(diary, t), [diary, t]);
	const collapseEarlierRounds = rounds.length > 1;

	return (
		<div className={collapseEarlierRounds ? "" : "h-650"}>
			{rounds.map((round, index) => {
				const isLast = index === rounds.length - 1;
				const collapsible = collapseEarlierRounds && !isLast;
				const expanded = !collapsible || openRounds[round.roundIndex] === true;
				const story = collapsible
					? (stories[round.roundIndex] || fallbackStory(round.weightChartData, t))
					: null;
				const previousOpen = index > 0 && openRounds[rounds[index - 1].roundIndex] === true;
				return (
					<div
						key={`chart-round-${round.roundIndex}`}
						data-testid={previousOpen ? "chart-current-round-separator" : undefined}
						className={`${previousOpen ? "chart-round-break" : index > 0 ? "mt-4" : ""} ${collapseEarlierRounds ? "" : "h-full flex flex-col"}`}
					>
						{story ? (
							<button
								type="button"
								data-testid={`chart-round-${round.roundIndex}`}
								onClick={() => setOpenRounds((current) => ({
									...current,
									[round.roundIndex]: !current[round.roundIndex],
								}))}
								className={`weight-round-summary relative w-full bg-green text-white text-center text-sm font-semibold py-3 px-10 ${index > 0 ? "border-t border-white" : ""}`}
							>
								<span data-testid={`chart-round-${round.roundIndex}-story`}>
									{story}
								</span>
								<span
									className={`absolute top-0 bottom-0 flex items-center mt-0 ${isRtl ? "left-4" : "right-4"}`}
									style={{marginTop: 0}}
								>
									<img
										src={SelectArrow}
										alt=""
										className={`block w-3 h-2 transform ${expanded ? "rotate-180" : ""}`}
									/>
								</span>
							</button>
						) : null}
						{expanded ? (
							<RoundChart
								round={round}
								valueGap={valueGap}
								tall={!collapseEarlierRounds}
								t={t}
							/>
						) : null}
					</div>
				);
			})}
		</div>
	);
}

function RoundChart({round, valueGap, tall, t}) {
	const points = round.weightChartData || [];
	const data = {
		labels: points.map((point) => point.x),
		datasets: [
			{
				label: "",
				data: points.map((point) => point.y),
				borderColor: "#7AC142",
				backgroundColor: "#7AC142",
			},
		],
	};

	return (
		<div
			data-testid={`chart-round-${round.roundIndex}-stats`}
			className={tall ? "h-full flex flex-col" : ""}
		>
			<div className={"flex justify-between"}>
				<div className={"m-0 flex items-end"}>
					<p className={"text-xs text-gray text-end"}>{t("weight_graph_loading")}:</p>
					<p className={`text-sm ${valueGap} leading-4`}>{round.dietSummaryLoadingValue}</p>
				</div>
				<div className={"m-0 flex items-end"}>
					<p className={"text-xs text-gray"}>{t("weight_graph_loss")}:</p>
					<p className={`text-sm ${valueGap} leading-4`}>{round.dietSummaryDietPhaseValue}</p>
				</div>
				<div className={"m-0 flex items-end"}>
					<p className={"text-xs text-gray"}>{t("weight_graph_average")}:</p>
					<p className={`text-sm ${valueGap} leading-4`}>{round.dietSummaryAverageLossValue}</p>
				</div>
				<div className={"m-0 flex items-end"}>
					<p className={"text-xs text-gray"}>{t("weight_graph_stabilize")}:</p>
					<p className={`text-sm ${valueGap} leading-4`}>
						{round.dietSummaryStabilizationWeightValue}
					</p>
				</div>
			</div>
			{points.length > 0 ? (
				<div className={`relative shadow w-full rounded-xl mt-5 p-3 ${tall ? "flex-1 min-h-0" : "h-460"}`}>
					<Line options={chartOptions} data={data}/>
				</div>
			) : null}
		</div>
	);
}

export function chartRounds(response) {
	if (response && Array.isArray(response.rounds) && response.rounds.length > 0) {
		return response.rounds;
	}
	if (!response) {
		return [];
	}
	return [{
		roundIndex: 0,
		dietSummaryLoadingValue: response.dietSummaryLoadingValue,
		dietSummaryDietPhaseValue: response.dietSummaryDietPhaseValue,
		dietSummaryAverageLossValue: response.dietSummaryAverageLossValue,
		dietSummaryStabilizationWeightValue: response.dietSummaryStabilizationWeightValue,
		weightChartData: response.weightChartData || [],
	}];
}

function storiesByRound(rows, t) {
	if (!diaryRowsHaveRounds(rows)) {
		return {};
	}
	const groups = groupDiaryRounds(rows);
	if (groups.length < 2) {
		return {};
	}
	const stories = {};
	groups.forEach((group, index) => {
		if (index === groups.length - 1) {
			return;
		}
		const summary = summarizeRound(group.rows);
		if (summary) {
			stories[group.roundIndex] = roundStory(summary, t);
		}
	});
	return stories;
}

function fallbackStory(points, t) {
	const weighed = (points || []).filter((point) => !Number.isNaN(parseFloat(point.y)));
	if (weighed.length === 0) {
		return "";
	}
	const start = weighed[0];
	const end = weighed[weighed.length - 1];
	return roundStory({
		startDate: start.x,
		startWeight: parseFloat(start.y),
		endDate: end.x,
		endWeight: parseFloat(end.y),
		loss: null,
	}, t);
}
